/**
 * Client analytics — batches events to `POST /api/analytics/events`.
 *
 * Replaces the prior console-only stub. The sink persists to
 * `user_event_activities` via the Express telemetry router.
 */

import {
  flushQueue,
  initAnalyticsSink,
  sendAnalyticsEvent,
} from './analytics-sink';

export type { AnalyticsUserContext } from './analytics-sink';

/**
 * Initialize analytics with user context. Call once after auth bootstrap.
 */
export function initAnalytics(user?: {
  userId: number;
  email: string;
  companyId: number;
}): void {
  initAnalyticsSink(user);
}

/**
 * Track a named event with optional properties.
 */
export function trackEvent(name: string, properties?: Record<string, unknown>): void {
  sendAnalyticsEvent(name, properties);
}

/**
 * Track a page view.
 */
export function trackPageView(path: string, title?: string): void {
  trackEvent('page_view', { path, title: title ?? document.title });
}

/**
 * Track a search query.
 */
export function trackSearch(query: string, resultCount: number): void {
  trackEvent('search', { query, resultCount });
}

/** Flush pending events immediately (e.g. before navigation). */
export function flushAnalytics(): Promise<void> {
  return flushQueue();
}

/** @internal Test-only reset. */
export { resetAnalyticsSinkForTests, getAnalyticsQueueDepthForTests } from './analytics-sink';
